import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, normalizeLob } from '../lib/api';
import { useAuth, canAccessLob } from './AuthContext';

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [lobs, setLobs] = useState([]);
  const [currentLobId, setCurrentLobId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lobsError, setLobsError] = useState(false);

  const reloadLobs = useCallback(async (silent = false) => {
    if (!user) { setLobs([]); setLobsError(false); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/lobs');
      const normalized = data.map(normalizeLob);
      setLobs(normalized);
      setLobsError(false);
      // Pick first accessible, active LOB if none selected or current is no longer accessible/active
      const accessible = normalized.filter((l) => canAccessLob(user, l.id) && l.is_active !== false);
      const list = accessible.length ? accessible : normalized.filter((l) => canAccessLob(user, l.id));
      const fallback = list.length ? list : normalized;
      setCurrentLobId((prev) => (prev && fallback.some((l) => l.id === prev)) ? prev : (fallback[0]?.id || null));
    } catch {
      setLobs([]);
      setLobsError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user]);

  useEffect(() => { reloadLobs(); }, [reloadLobs]);

  const currentLob = lobs.find((l) => l.id === currentLobId) || null;

  return (
    <DataContext.Provider value={{ lobs, currentLob, currentLobId, setCurrentLobId, reloadLobs, loading, lobsError }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
